import Phaser from 'phaser';
import { COLORS, CSS, FONT, RADIUS, toRgba } from '../theme';

export interface PillOpts {
  fill?: number;
  fillAlpha?: number;
  text: string;
  textColor?: string;
  textSize?: number;
  fontFamily?: string;
  fontWeight?: string;
  padX?: number;
  padY?: number;
  border?: number;
  borderAlpha?: number;
  borderThickness?: number;
}

/**
 * Glassy/translucent pill with optional outline. Used for HUD chips
 * (stage name, BPM readout, "DAILY 14:23", count badges, etc.).
 *
 * Returns a Container anchored on (x, y). Width is sized to content.
 */
export function makePill(
  scene: Phaser.Scene,
  x: number,
  y: number,
  opts: PillOpts,
): Phaser.GameObjects.Container {
  const fillColor = opts.fill ?? COLORS.white;
  const fillAlpha = opts.fillAlpha ?? 0.12;
  // Padding scales with the larger 28px default text so the pill keeps its
  // chunky cartoon proportions instead of becoming a thin strip when text grows.
  const padX = opts.padX ?? 28;
  const padY = opts.padY ?? 14;
  const borderThickness = opts.borderThickness ?? 0;

  const text = scene.add
    .text(0, 0, opts.text, {
      fontFamily: opts.fontFamily ?? FONT.display,
      fontSize: `${opts.textSize ?? 28}px`,
      color: opts.textColor ?? CSS.white,
      fontStyle: opts.fontWeight ?? '700',
    })
    .setOrigin(0.5);

  const w = text.width + padX * 2;
  const h = text.height + padY * 2;
  // Clamp the corner radius so a "pill-style" 999 stays within w/2 × h/2.
  // (Phaser's fillRoundedRect does NOT auto-clamp; an oversized radius produces
  // arc paths that extend beyond the rect bounds and leave canvas-wide artifacts.)
  const r = Math.min(RADIUS.pill, w / 2, h / 2);

  const g = scene.add.graphics();
  g.fillStyle(fillColor, fillAlpha);
  g.fillRoundedRect(-w / 2, -h / 2, w, h, r);
  if (borderThickness > 0) {
    const borderC = opts.border ?? COLORS.white;
    g.lineStyle(borderThickness, borderC, opts.borderAlpha ?? 1);
    g.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
  }

  // Use a slightly inset background hint for glass effect by drawing a faint top highlight.
  // (Phaser has no real blur; this lightens the top edge.)
  const hlR = Math.max(0, r - 2);
  const hl = scene.add.graphics();
  hl.fillStyle(COLORS.white, 0.07);
  hl.fillRoundedRect(-w / 2 + 2, -h / 2 + 2, w - 4, h * 0.45, hlR);

  const container = scene.add.container(x, y, [g, hl, text]);
  return container;
}

/** Compose multiple text segments separated by faint vertical bars (like "90  |  BPM  |  4/4"). */
export interface CompoundPillSegment {
  text: string;
  color?: string;
  size?: number;
  fontFamily?: string;
  weight?: string;
  letterSpacing?: string;
  tabular?: boolean;
}

export function makeCompoundPill(
  scene: Phaser.Scene,
  x: number,
  y: number,
  segments: CompoundPillSegment[],
  pillOpts: Partial<PillOpts> = {},
): Phaser.GameObjects.Container {
  const padX = pillOpts.padX ?? 28;
  const padY = pillOpts.padY ?? 14;
  const gap = 16;

  // Render each segment, then position by computing total width.
  const parts: Phaser.GameObjects.Text[] = segments.map((s) =>
    scene.add
      .text(0, 0, s.text, {
        fontFamily: s.fontFamily ?? FONT.display,
        fontSize: `${s.size ?? 28}px`,
        color: s.color ?? CSS.white,
        fontStyle: s.weight ?? '700',
      })
      .setOrigin(0, 0.5),
  );
  parts.forEach((p, i) => {
    if (segments[i].tabular) {
      p.setStyle({ fontStyle: segments[i].weight ?? '800' });
    }
  });

  let totalW = 0;
  parts.forEach((p, i) => {
    totalW += p.width;
    if (i < parts.length - 1) totalW += gap;
  });

  // Optional thin vertical dividers between segments (matches design).
  // The design separates segments with a vertical line; we add small graphics for that.
  // Build container first, then layout.
  const container = scene.add.container(x, y);
  const h = Math.max(...parts.map((p) => p.height)) + padY * 2;
  const w = totalW + padX * 2;
  // Same clamp as makePill — keep capsule radius within rect bounds.
  const r = Math.min(RADIUS.pill, w / 2, h / 2);

  const bg = scene.add.graphics();
  bg.fillStyle(pillOpts.fill ?? COLORS.white, pillOpts.fillAlpha ?? 0.1);
  bg.fillRoundedRect(-w / 2, -h / 2, w, h, r);
  bg.lineStyle(pillOpts.borderThickness ?? 2, pillOpts.border ?? COLORS.white, pillOpts.borderAlpha ?? 0.2);
  bg.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
  container.add(bg);

  let cursor = -totalW / 2;
  parts.forEach((p, i) => {
    p.setPosition(cursor, 0);
    cursor += p.width + gap;
    container.add(p);
    if (i < parts.length - 1) {
      // Divider line
      const d = scene.add.graphics();
      d.fillStyle(COLORS.white, 0.25);
      d.fillRect(cursor - gap / 2 - 1, -h * 0.25, 2, h * 0.5);
      container.add(d);
    }
  });

  // subtle highlight strip
  const hl = scene.add.graphics();
  hl.fillStyle(COLORS.white, 0.06);
  hl.fillRoundedRect(-w / 2 + 2, -h / 2 + 2, w - 4, h * 0.45, Math.max(0, r - 2));
  container.addAt(hl, 1);

  // Apply a soft outer border for the glass look using a second pass (rgba string isn't directly usable in Graphics)
  void toRgba; // referenced for tree-shake safety

  return container;
}
